import { useCallback, useMemo, useState } from "react";

import type {
  Facet,
  FacetSelection,
  FacetValueOption,
  UseFacetFilterConfig,
  UseFacetFilterResult
} from "./types";

export type { UseFacetFilterConfig, UseFacetFilterResult } from "./types";

const ALL_GROUP_KEY = "__all__";
const UNSORTED_GROUP_KEY = "__unsorted__";

const hasFacetValue = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.length > 0;

const selectedValuesFor = (
  selection: FacetSelection,
  facetKey: string
): readonly string[] => selection[facetKey] ?? [];

export function useFacetFilter<T>(
  config: UseFacetFilterConfig<T>
): UseFacetFilterResult<T> {
  const { items, facetDefs, labelFor } = config;
  const [selection, setSelection] = useState<FacetSelection>({});
  const [groupBy, setGroupByState] = useState<string | null>(
    config.initialGroupBy ?? null
  );

  const facetDefByKey = useMemo(() => {
    const byKey = new Map<string, UseFacetFilterConfig<T>["facetDefs"][number]>();

    for (const facetDef of facetDefs) {
      byKey.set(facetDef.key, facetDef);
    }

    return byKey;
  }, [facetDefs]);

  const matchesSelection = useCallback(
    (item: T, selectionToApply: FacetSelection, exceptFacetKey?: string): boolean => {
      for (const facetDef of facetDefs) {
        if (facetDef.key === exceptFacetKey) {
          continue;
        }

        const selected = selectedValuesFor(selectionToApply, facetDef.key);

        if (selected.length === 0) {
          continue;
        }

        const itemValue = facetDef.valueOf(item);

        if (!hasFacetValue(itemValue) || !selected.includes(itemValue)) {
          return false;
        }
      }

      return true;
    },
    [facetDefs]
  );

  const filtered = useMemo(
    () => items.filter((item) => matchesSelection(item, selection)),
    [items, matchesSelection, selection]
  );

  const facets = useMemo<Facet[]>(() => {
    return facetDefs.map((facetDef) => {
      const orderedValues: string[] = [];
      const allValueCounts = new Map<string, number>();
      const liveValueCounts = new Map<string, number>();

      for (const item of items) {
        const value = facetDef.valueOf(item);

        if (!hasFacetValue(value)) {
          continue;
        }

        if (!allValueCounts.has(value)) {
          orderedValues.push(value);
        }

        allValueCounts.set(value, (allValueCounts.get(value) ?? 0) + 1);

        if (matchesSelection(item, selection, facetDef.key)) {
          liveValueCounts.set(value, (liveValueCounts.get(value) ?? 0) + 1);
        }
      }

      const values: FacetValueOption[] = orderedValues.map((value) => {
        const tone = facetDef.toneOf?.(value);
        const option = {
          value,
          label: labelFor(facetDef.key, value),
          count: liveValueCounts.get(value) ?? 0
        };

        return tone ? { ...option, tone } : option;
      });
      const facet = {
        key: facetDef.key,
        label: facetDef.label,
        values
      };

      return facetDef.multiSelect === undefined
        ? facet
        : { ...facet, multiSelect: facetDef.multiSelect };
    });
  }, [facetDefs, items, labelFor, matchesSelection, selection]);

  const toggle = useCallback(
    (facetKey: string, value: string) => {
      setSelection((current) => {
        const facetDef = facetDefByKey.get(facetKey);

        if (!facetDef) {
          return current;
        }

        const selected = current[facetKey] ?? [];
        const isSelected = selected.includes(value);
        const next = { ...current };

        if (facetDef.multiSelect === false) {
          if (isSelected && selected.length === 1) {
            delete next[facetKey];
          } else {
            next[facetKey] = [value];
          }

          return next;
        }

        const nextSelected = isSelected
          ? selected.filter((selectedValue) => selectedValue !== value)
          : [...selected, value];

        if (nextSelected.length === 0) {
          delete next[facetKey];
        } else {
          next[facetKey] = nextSelected;
        }

        return next;
      });
    },
    [facetDefByKey]
  );

  const clearAll = useCallback(() => {
    setSelection({});
  }, []);

  const setGroupBy = useCallback(
    (facetKey: string | null) => {
      if (facetKey === null || facetDefByKey.has(facetKey)) {
        setGroupByState(facetKey);
      } else {
        setGroupByState(null);
      }
    },
    [facetDefByKey]
  );

  const groups = useMemo<UseFacetFilterResult<T>["groups"]>(() => {
    if (groupBy === null) {
      return [{ key: ALL_GROUP_KEY, label: "All", items: [...filtered] }];
    }

    const facetDef = facetDefByKey.get(groupBy);

    if (!facetDef) {
      return [{ key: ALL_GROUP_KEY, label: "All", items: [...filtered] }];
    }

    const grouped = new Map<string, { key: string; label: string; items: T[] }>();

    for (const item of filtered) {
      const value = facetDef.valueOf(item);
      const key = hasFacetValue(value) ? value : UNSORTED_GROUP_KEY;
      const label = hasFacetValue(value) ? labelFor(groupBy, value) : "Unsorted";
      const existing = grouped.get(key);

      if (existing) {
        existing.items.push(item);
      } else {
        grouped.set(key, { key, label, items: [item] });
      }
    }

    return [...grouped.values()];
  }, [facetDefByKey, filtered, groupBy, labelFor]);

  return {
    facets,
    selection,
    toggle,
    clearAll,
    filtered,
    groupBy,
    setGroupBy,
    groups
  };
}
